import { useState } from 'react';
import { Alert } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../../shared/subscriptions/utils';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import useGetApiWebhookSettings from '../../../../hooks/api/settings/integrations/useGetApiWebhookSettings';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import Button from '../../../generic/Button/Button';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import NavigationButton from '../../../generic/Button/NavigationButton';
import { icons } from '../../../generic/Icon/icons';
import { Icon } from '../../../generic/Icon/index';
import { DOCS_URL } from '../../../generic/Link/DocsLink';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import type { NotificationWebhookResponse } from '../../../../../shared/settings/apiWebhooks';

import ApiWebhookRow from './ApiWebhookRow';
import CreateApiWebhookModal from './CreateApiWebhookModal';

const apiWebhooksDocsLink = `${DOCS_URL}/api-webhooks`;

type SettingsBodyProps = {
  apiWebhooks: NotificationWebhookResponse[];
  isLoading: boolean;
};

type AddWebhookButtonProps = {
  onClick: () => void;
};

const AddWebhookButton: VoidFunctionComponent<AddWebhookButtonProps> = ({ onClick }) => {
  if (isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  })) {
    return (
      <Button
        onClick={() => {
          window.showPlanSelectorModal({ focusFeature: 'slackIntegrationFeature' });
        }}
        type={BUTTON_TYPE.SECONDARY}
      >
        <Icon icon={icons.lockOutline} /> Add another Webhook
      </Button>
    );
  }

  return (
    <Button
      type={BUTTON_TYPE.SECONDARY}
      onClick={onClick}
    >
      <Icon icon={icons.add} /> Add Webhook
    </Button>
  );
};

const SettingsBody: VoidFunctionComponent<SettingsBodyProps> = ({ apiWebhooks, isLoading }) => {
  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      {apiWebhooks.map((webhook) =>
        <ApiWebhookRow webhook={webhook} />,
      )}
    </>
  );
};

const ApiWebhookSettings: VoidFunctionComponent = () => {
  const hasPermission = useHasPermission();
  const { data, isLoading } = useGetApiWebhookSettings(window.user?.organizationId ?? 0);

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!hasPermission({
    resourceType: ResourceType.OrganizationIntegrationSettings,
    action: Action.Edit,
  })
  ) {
    return (
      <Alert bsStyle="info">
        You are not permitted to edit Slack settings.
      </Alert>
    );
  }

  return (
    <>
      <Header heading="API Webhook Settings" />
      <Section>
        <Section.Left heading="API Webhook Settings">
          <Section.Paragraph>
            Configure your API Webhook notification settings
          </Section.Paragraph>
          <NavigationButton
            to={apiWebhooksDocsLink}
            size="medium"
            type="secondary"
            openInNewTab
          >
            View documentation
          </NavigationButton>
        </Section.Left>
        <Section.Right>
          <SettingsBody
            apiWebhooks={data ?? []}
            isLoading={isLoading}
          />
          <AddWebhookButton onClick={() => setIsModalOpen(true)} />
        </Section.Right>
      </Section>
      <CreateApiWebhookModal isShowing={isModalOpen} onHide={() => setIsModalOpen(false)} />
    </>
  );
};

export default ApiWebhookSettings;
